/**
 * Error Helper Utilities
 *
 * Centralized error parsing and handling logic to eliminate duplication
 * across CRUD operations in different modules.
 */

/**
 * Backend error code from an API error (response body `error.code`), e.g. "CONTACT_PHONE_EXISTS"
 */
export const getApiErrorCode = (error: any): string | undefined =>
  error?.data?.error?.code ?? error?.code;

// POST/PUT api/contacts: the phone number already belongs to another contact (409)
export const CONTACT_PHONE_EXISTS = 'CONTACT_PHONE_EXISTS';

/**
 * For CONTACT_PHONE_EXISTS: the id of the existing contact when the current user can access it.
 * Missing when that contact is assigned to another agent.
 */
export const getExistingContactId = (error: any): string | undefined =>
  error?.data?.error?.details?.existingContactId ?? undefined;

/**
 * Error pattern matching configuration
 */
interface ErrorPattern {
  pattern: string | RegExp;
  errorCode: string;
}

/**
 * Parse API error and throw standardized error codes
 *
 * @param error - Error object from API call
 * @param patterns - Array of error patterns to match against
 * @throws Error with standardized error code if pattern matches
 * @throws Original error if no pattern matches
 *
 * @example
 * try {
 *   await createContact(data).unwrap();
 * } catch (err) {
 *   parseApiError(err, [
 *     { pattern: /phone.*exists/i, errorCode: 'PHONE_ALREADY_EXISTS' },
 *     { pattern: /email.*exists/i, errorCode: 'EMAIL_ALREADY_EXISTS' },
 *   ]);
 * }
 */
export function parseApiError(error: any, patterns: ErrorPattern[]): never {
  const msg = (error?.data?.message || error?.message || '').toLowerCase();

  for (const { pattern, errorCode } of patterns) {
    const regex = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern;
    if (regex.test(msg)) {
      throw new Error(errorCode);
    }
  }

  throw error;
}

/**
 * Common error patterns for contact/account modules
 */
export const COMMON_DUPLICATE_ERRORS: ErrorPattern[] = [
  { pattern: /phone.*exists/i, errorCode: 'PHONE_ALREADY_EXISTS' },
  { pattern: /email.*exists/i, errorCode: 'EMAIL_ALREADY_EXISTS' },
];

/**
 * Account-specific error patterns (includes CR number)
 */
export const ACCOUNT_DUPLICATE_ERRORS: ErrorPattern[] = [
  ...COMMON_DUPLICATE_ERRORS,
  { pattern: /crnumber.*exists/i, errorCode: 'CR_NUMBER_ALREADY_EXISTS' },
];

/**
 * Wrapper for handling duplicate errors in contact operations
 *
 * @param error - Error from API call
 * @throws Standardized error code
 */
export function handleContactDuplicateError(error: any): never {
  return parseApiError(error, COMMON_DUPLICATE_ERRORS);
}

/**
 * Wrapper for handling duplicate errors in account operations
 *
 * @param error - Error from API call
 * @throws Standardized error code
 */
export function handleAccountDuplicateError(error: any): never {
  return parseApiError(error, ACCOUNT_DUPLICATE_ERRORS);
}
